import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { ISpecialty } from '../../shared/interfaces';
import SpecialtyCreateEdit from './CreateEdit';
import { getSpecialties } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Specialties: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);
  const [specialty, setSpecialty] = useState<ISpecialty | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listSpecialties = useCallback(async () => {
    setLoading(true);
    setSpecialties([]);
    try {
      const response = await getSpecialties(limit, (page - 1) * limit, search);
      setPages(response.count);
      setSpecialties(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar especialidades',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalSpecialtyCreateEdit = () => {
    if (modalCreate)
      return (
        <SpecialtyCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          specialty={specialty}
          setSpecialties={setSpecialties}
        />
      );
  };

  useEffect(() => {
    listSpecialties();
  }, [page, search, limit, listSpecialties]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<SafetyCertificateOutlined />}
        title="Especialidades"
        description="Organize as especialidades da clínica e mantenha a base disponível para os cadastros de profissionais e atendimentos."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listSpecialties(),
          },
          permissionFind([PermissionActions.specialtiesCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setSpecialty(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 80,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: ISpecialty) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.specialtiesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setSpecialty(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={specialties}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalSpecialtyCreateEdit()}
    </PageLayout>
  );
};

export default Specialties;
